import {
  ChangeDetectorRef, Component, ElementRef, inject,
  HostListener,
  Inject, PLATFORM_ID, Renderer2, ViewChild,
} from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { UserDropdown } from '../../components/header/user-dropdown/user-dropdown';
import { Notification } from '../../components/header/notification/notification';
import { ShareLoveMenu } from '../../components/header/share-love-menu/share-love-menu';
import { CreateContentModals } from '../../components/create-content/create-content-modals';
import { AuthStateService } from '../../shared/states/auth.state';
import { filter, Observable, Subject, debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ChatService } from '../../shared/services/chat.service';
import { SearchService } from '../../shared/services/search.service';

@Component({
  selector: 'app-app-header',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    UserDropdown,
    Notification,
    ShareLoveMenu,
    CreateContentModals,
  ],
  templateUrl: './app-header.html',
})
export class AppHeader {

  isApplicationMenuOpen = false;
  isMobileSearchOpen = false;
  readonly isMobileOpen$;
  authState$!: Observable<any>;
  authLoaded = false;
  currentUrl = '';
  isNotificationPanelOpen = false;
  get unreadNotifCount$() { return this.notificationService.unreadCount$; }
  private chatService = inject(ChatService);
  get totalUnreadChat$() { return this.chatService.totalUnread$; }

  isLoggingOut = false;

  // ── GLOBAL SEARCH (header search box) ────────────────────────────────────
  globalSearchText = '';
  globalSearchResults: any[] = [];
  /** People whose display name/username matched the query — shown as a
   *  "Profiles" section above the normal post/blog/event results. */
  globalSearchProfiles: any[] = [];
  globalSearchLoading = false;
  showGlobalSearchDrop = false;
  private globalSearch$ = new Subject<string>();

  // ── VIEW CHILDREN ────────────────────────────────────────────────────────

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mobileSearchInputRef') mobileSearchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild(UserDropdown)  userDropdown?: UserDropdown;
  @ViewChild(ShareLoveMenu) shareMenu?: ShareLoveMenu;
  /** Shared Create post / Create event dialogs (also used by AppDashboardHeader). */
  @ViewChild(CreateContentModals) createContent?: CreateContentModals;

  private removeKeydownListener!: () => void;

  // ── CONSTRUCTOR ──────────────────────────────────────────────────────────

  constructor(
    public sidebarService: SidebarService,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
    private authState: AuthStateService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private notificationService: NotificationService,
    private searchService: SearchService,
  ) {
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.authState$ = this.authState.auth$;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentUrl = event.url;
        this.closeApplicationMenu();
        this.closeDropdown();
      });

    this.authState$.subscribe(() => { this.authLoaded = true; });

    this.globalSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(q => this.runGlobalSearch(q));
  }

  // ── LIFECYCLE ────────────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      this.isApplicationMenuOpen &&
      !target.closest('[data-app-menu-panel]') &&
      !target.closest('[data-app-menu-toggle]')
    ) {
      this.closeApplicationMenu();
    }

    if (this.showGlobalSearchDrop && !target.closest('[data-global-search-section]')) {
      this.showGlobalSearchDrop = false;
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.removeKeydownListener = this.renderer.listen('document', 'keydown', this.handleKeyDown);
    }
  }

  ngOnDestroy() {
    this.closeApplicationMenu();
    if (this.removeKeydownListener) this.removeKeydownListener();
  }

  // ── SHARED HELPERS ───────────────────────────────────────────────────────

  handleToggle() {
    if (isPlatformBrowser(this.platformId)) {
      window.innerWidth >= 1280
        ? this.sidebarService.toggleExpanded()
        : this.sidebarService.toggleMobileOpen();
    }
  }

  toggleApplicationMenu() { this.setApplicationMenuState(!this.isApplicationMenuOpen); }
  closeApplicationMenu() { this.setApplicationMenuState(false); }
  closeDropdown() { this.shareMenu?.close(); }
  /** "Share the love" opened — close the user menu so only one is showing. */
  onShareMenuOpened() { this.userDropdown?.closeDropdown(); }
  toggleNotificationPanel() { this.isNotificationPanelOpen = !this.isNotificationPanelOpen; }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closeApplicationMenu();
      this.closeDropdown();
      this.closeMobileSearch();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  };

  private setApplicationMenuState(isOpen: boolean): void {
    this.isApplicationMenuOpen = isOpen;
    if (!isOpen) this.closeDropdown();
    else this.closeMobileSearch();
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.setStyle(document.body, 'overflow', isOpen ? 'hidden' : '');
    }
  }

  openMobileSearch(): void {
    this.closeApplicationMenu();
    this.isMobileSearchOpen = true;
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
    }
    setTimeout(() => this.mobileSearchInputRef?.nativeElement.focus(), 50);
  }

  closeMobileSearch(): void {
    if (!this.isMobileSearchOpen) return;
    this.isMobileSearchOpen = false;
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.setStyle(document.body, 'overflow', '');
    }
  }

  // ── CREATE POST / EVENT (shared dialogs) ─────────────────────────────────

  openPostModal() {
    this.closeApplicationMenu();
    this.createContent?.openPostModal();
  }

  openEventModal() {
    this.closeApplicationMenu();
    this.createContent?.openEventModal();
  }

  // ── SIGN OUT ─────────────────────────────────────────────────────────────

  signOut() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    const clientId = this.authState.getClientId();
    const redirectUrl = this.authState.getLogoutRedirectUrl();
    this.authService.logout(clientId ?? undefined)
      .pipe(finalize(() => { this.isLoggingOut = false; }))
      .subscribe({
        next: () => { this.authState.clearAuth(); this.closeDropdown(); if (typeof window !== 'undefined') window.location.href = redirectUrl; },
        error: () => { this.authState.clearAuth(); this.closeDropdown(); if (typeof window !== 'undefined') window.location.href = redirectUrl; }
      });
  }

  // ── GLOBAL SEARCH (posts + blogs + events, by title or #hashtag) ──────────

  onGlobalSearchFocus(): void {
    this.showGlobalSearchDrop = true;
    if (this.globalSearchText) this.globalSearch$.next(this.globalSearchText);
  }

  onGlobalSearchInput(value: string): void {
    this.showGlobalSearchDrop = true;
    if (!value.trim()) {
      this.globalSearchResults = [];
      this.globalSearchProfiles = [];
      this.globalSearchLoading = false;
      return;
    }
    this.globalSearch$.next(value);
  }

  private runGlobalSearch(query: string): void {
    const raw = query.trim();
    if (!raw) { this.globalSearchResults = []; this.globalSearchProfiles = []; return; }

    this.globalSearchLoading = true;
    const isHashtag = raw.startsWith('#');
    const filters = isHashtag
      ? { hashtag: raw.replace(/^#+/, '') }
      : { q: raw };

    this.searchService.search(1, 6, filters).subscribe({
      next: (res: any) => {
        this.globalSearchResults = res?.data?.items ?? [];
        // Only free-text (`q`) searches return matched people — a #hashtag
        // lookup never populates `profiles` on the backend.
        this.globalSearchProfiles = res?.data?.profiles ?? [];
        this.globalSearchLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.globalSearchResults = [];
        this.globalSearchProfiles = [];
        this.globalSearchLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  closeGlobalSearchDrop(): void {
    this.showGlobalSearchDrop = false;
  }

  clearGlobalSearch(): void {
    this.globalSearchText = '';
    this.globalSearchResults = [];
    this.globalSearchProfiles = [];
    this.showGlobalSearchDrop = false;
  }

  /** Jump straight to a matched person's profile from the search dropdown. */
  goToGlobalSearchProfile(profile: any): void {
    this.showGlobalSearchDrop = false;
    this.globalSearchText = '';
    this.closeMobileSearch();
    if (!profile?.userName) return;
    this.router.navigate(['/profile', profile.userName, 'post']);
  }

  /** Placeholder avatar for a matched profile that has no profile image yet —
   *  same ui-avatars.com pattern used elsewhere across the app. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  /** Navigates to the "All" page with the current query/hashtag applied. */
  submitGlobalSearch(): void {
    const raw = this.globalSearchText.trim();
    if (!raw) return;
    this.showGlobalSearchDrop = false;

    const isHashtag = raw.startsWith('#');
    const value = raw.replace(/^#+/, '');

    this.router.navigate(['/all'], {
      queryParams: isHashtag ? { hashtag: value } : { q: value },
    });
  }

  /** Jump straight to a specific search result's detail page. */
  selectGlobalSearchResult(item: any): void {
    this.showGlobalSearchDrop = false;
    this.globalSearchText = '';
    const id = item?._id;
    if (!id) return;

    if (item.type === 'POST') this.router.navigate(['/post', id]);
    else if (item.type === 'BLOG') this.router.navigate(['/blog', id]);
    else if (item.type === 'EVENT') this.router.navigate(['/event', id]);
  }
}
